import type { Manifest } from '@clawharbor/manifest';
import { html, SP } from '../html.js';
import type { VNode } from '../html.js';
import type { Actions } from '../actions.js';
import type { ConsoleState } from '../store.js';
import type { Clearance } from '../types.js';
import { BrandFrame } from '../ui/brandframe.js';
import { CapabilityList } from '../ui/caps.js';
import { Endpoints } from '../ui/endpoints.js';
import { PublishGate } from '../ui/gate.js';
import { Clearances } from '../ui/ledger.js';
import { BrandSpec } from '../ui/spec.js';

/**
 * Review, then the gate.
 *
 * Nothing here is a summary of the manifest — it is the manifest, rendered: the
 * palette painted by the production token emitter, the descriptions an agent
 * will actually read, and the verdicts verbatim.
 *
 * Two outcomes, not one. A crawl that verified nothing is the COMMON outcome —
 * docs/COVERAGE.md puts a usable capability on 5 of 51 sites — so it gets a
 * written screen that says what was found, what was not, and what to try, not
 * an empty list between two rules with a "Publish 0 tools" button under it.
 */
export function ResultScreen(props: { state: ConsoleState; actions: Actions }): VNode {
  const { state, actions } = props;
  const m = state.manifest;
  if (!m) {
    return html`
      <div class="wrap section">
        <div class="fatal" role="alert">
          <p class="fatal__h">No manifest came back</p>
          <p class="fatal__p">
            The crawl reported that it finished, but the manifest could not be read from the job
            store. Nothing was published and nothing was changed.
          </p>
          <button type="button" class="btn btn--ghost" onClick=${() => actions.restart()}>
            Start over
          </button>
        </div>
      </div>
    `;
  }

  const turnedBack = state.clearances.filter((c) => c.verdict === 'turned-back');
  const cleared = m.capabilities.length;

  return html`
    <div>
      ${BrandFrame({ manifest: m, cleared, turnedBack: turnedBack.length })}

      ${state.published
        ? html`
            <section class="wrap section" style="padding-top:clamp(30px,4vw,44px)">
              <div class="published">
                <div class="bigstamp">
                  Cleared for publication<small>${m.id} · ${cleared} tools live</small>
                </div>
                <p class="published__p">
                  Both surfaces are serving this manifest. Re-crawl any time to issue a new version;
                  the URLs stay the same.
                </p>
              </div>
              <p class="eyebrow" style="margin-top:30px">Live endpoints</p>
              ${Endpoints({
                webUrl: state.published.webUrl,
                mcpUrl: state.published.mcpUrl,
                tenantId: m.id,
                copied: state.copied,
                onCopy: actions.copy,
              })}
            </section>
          `
        : null}

      ${cleared === 0 ? BarrenNotice({ manifest: m, clearances: state.clearances }) : null}

      <section class="wrap section">
        <p class="eyebrow">
          Brand<span class="eyebrow__count">extracted from ${m.source.canonicalOrigin}</span>
        </p>
        ${BrandSpec({ manifest: m })}
      </section>

      ${cleared
        ? html`
            <section class="wrap section">
              <p class="eyebrow">
                Capabilities<span class="eyebrow__count"
                  >${cleared} verified against the live origin</span
                >
              </p>
              ${CapabilityList({ capabilities: m.capabilities })}
            </section>
          `
        : null}

      ${turnedBack.length
        ? html`
            <section class="wrap section">
              <p class="eyebrow">
                Turned back at the gate<span class="eyebrow__count"
                  >${turnedBack.length} not published</span
                >
              </p>
              ${Clearances({ rows: turnedBack, standalone: true })}
              <p class="note">
                These were proposed, executed against ${m.source.canonicalOrigin}, and returned
                nothing usable. They are recorded here and excluded from both surfaces — an agent
                will never see them.
              </p>
            </section>
          `
        : null}

      ${state.published
        ? html`
            <section class="wrap section">
              <p class="eyebrow">Standing rules</p>
              <p class="note" style="margin-top:0">
                Data is read live and cached for${SP}
                ${Math.round((m.capabilities[0]?.freshness.ttlSec ?? 1800) / 60)} minutes, never
                retained as history. Outbound requests are capped at ${m.policy.data.maxRps}/s.
                Every transaction deep-links to ${m.source.canonicalOrigin}. Takedown:${SP}
                ${m.policy.takedownUrl}
              </p>
              <button
                type="button"
                class="btn btn--ghost"
                style="margin-top:22px"
                onClick=${() => actions.restart()}
              >
                Crawl another site
              </button>
            </section>
          `
        : cleared === 0
          ? html`
              <section class="wrap section">
                <p class="eyebrow">Publish</p>
                <div class="gate gate--shut">
                  <h2 class="gate__h">There is nothing to publish.</h2>
                  <p class="gate__p">
                    A manifest with no capabilities would serve an agent an empty tool list and a
                    palette it cannot use. Publishing is disabled rather than allowed and
                    disappointing.${SP}
                    ${state.clearances.length
                      ? 'Every proposal was executed against the live origin and every one was turned back; the reasons are above.'
                      : 'Nothing reached the gate, so nothing was refused — there was simply nothing to test.'}
                  </p>
                  <div class="gate__actions">
                    <button type="button" class="btn" onClick=${() => actions.restart()}>
                      Try a deeper URL
                    </button>
                    <a class="btn btn--ghost" href=${m.source.canonicalOrigin} target="_blank" rel="noopener noreferrer">
                      Open ${hostOf(m.source.canonicalOrigin)}
                    </a>
                  </div>
                </div>
              </section>
            `
          : html`
              <section class="wrap section">
                <p class="eyebrow">Publish</p>
                ${PublishGate({ state, actions, manifest: m, turnedBack })}

                <p class="eyebrow" style="margin-top:34px">Endpoints</p>
                ${Endpoints({
                  webUrl: null,
                  mcpUrl: null,
                  tenantId: m.id,
                  copied: state.copied,
                  onCopy: actions.copy,
                })}
              </section>
            `}
    </div>
  `;
}

/**
 * Why nothing cleared, in the crawl's own evidence.
 *
 * The diagnosis is read off the manifest rather than guessed: the rendering
 * mode, the page count, and whether anything reached the gate at all are enough
 * to separate the three failures that actually occur. Being specific is the
 * whole value — "no capabilities found" tells an operator nothing they can act
 * on, and it is what this screen used to say by saying nothing.
 */
function BarrenNotice(props: { manifest: Manifest; clearances: Clearance[] }): VNode {
  const m = props.manifest;
  const clientRendered = m.source.platform.rendering === 'client';
  const pages = m.source.crawl.pages;
  const allTurnedBack = props.clearances.length > 0;

  const headline = allTurnedBack
    ? 'Everything proposed was turned back.'
    : clientRendered
      ? 'This site builds itself in the browser.'
      : 'Nothing on this site read as a verb.';

  const because = allTurnedBack
    ? html`${props.clearances.length} bindings were synthesised and every one of them was executed
        against ${m.source.canonicalOrigin}. None returned a usable row, so none of them ship. The
        stamped reasons are below.`
    : clientRendered
      ? html`The HTML served to our crawler is a shell: the listings, the schedule and the prices are
          fetched by JavaScript after the page loads, and we read what the origin sends rather than
          executing an application on someone else's behalf. Brand extraction, which does drive a
          real browser, came back at high confidence — the two halves of this pipeline fail
          independently, and only one of them failed here.`
      : html`We read ${pages} ${pages === 1 ? 'page' : 'pages'} and found no listing, no form and no
          JSON endpoint that could be turned into something an agent could call. Contact details and
          prose are not verbs.`;

  return html`
    <section class="wrap section barren-section">
      <div class="barren">
        <span class="stamp stamp--turned barren__stamp">Nothing cleared</span>
        <h2 class="barren__h">${headline}</h2>
        <p class="barren__p">${because}</p>
        <p class="barren__p barren__p--muted">
          This is the ordinary result, not an error. Across a 51-site evaluation, 46 sites gave up a
          brand and 5 produced a capability an agent could usefully call. The gate refusing to ship
          a tool it could not verify is the product working.
        </p>

        <p class="barren__cap">What usually helps</p>
        <ol class="barren__list">
          <li>
            <div>
              <b>Point at the listing, not the homepage.</b>${SP}A menu, a schedule, a product
              index or a branch finder is where a verb lives. Paste that page's own URL rather than
              ${hostOf(m.source.canonicalOrigin)}.
            </div>
          </li>
          ${clientRendered
            ? html`<li>
                <div>
                  <b>Find the endpoint the page itself calls.</b>${SP}A client-rendered site fetches
                  its own JSON from somewhere. That URL, pasted here, is usually crawlable when the
                  page around it is not.
                </div>
              </li>`
            : html`<li>
                <div>
                  <b>Publish a sitemap or schema.org markup.</b>${SP}If this site is yours, an
                  <code>Organization</code> block and a sitemap are the two cheapest changes that
                  make it readable.
                </div>
              </li>`}
          <li>
            <div>
              <b>Run it again with model synthesis on.</b>${SP}This crawl used the heuristic
              proposer only, which is the floor — it names tools after markup and misses verbs a
              model would see.
            </div>
          </li>
        </ol>
      </div>
    </section>
  `;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
